import { pagePointToViewport } from "./geometry.js";
import { createAdjustableShapePath } from "./shape-adjust.js";
import type { PageSize, ShapeBounds, ShapePath } from "./types.js";

type NormalizedPoint = readonly [number, number];

const cubicCircleKappa = 0.5522847498307936;

/**
 * Pure polygon shapes, authored in normalized [0..1] space with Y UP
 * (y = 1 is the TOP edge, matching how `scalePoint` maps onto a PDF-space
 * bounds whose origin is bottom-left). Each is a closed straight-edge polygon.
 */
const normalizedPolygonShapes = {
  Rectangle: [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ],
  FlowChartProcess: [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ],
  Triangle: [
    [0.5, 1],
    [1, 0],
    [0, 0],
  ],
  RightTriangle: [
    [0, 1],
    [1, 0],
    [0, 0],
  ],
  Diamond: [
    [0.5, 1],
    [1, 0.5],
    [0.5, 0],
    [0, 0.5],
  ],
  FlowChartDecision: [
    [0.5, 1],
    [1, 0.5],
    [0.5, 0],
    [0, 0.5],
  ],
  // Parallelogram leaning right (top edge shifted right by ~0.25).
  Parallelogram: [
    [0.25, 1],
    [1, 1],
    [0.75, 0],
    [0, 0],
  ],
  FlowChartInputOutput: [
    [0.25, 1],
    [1, 1],
    [0.75, 0],
    [0, 0],
  ],
  // Trapezoid: narrower top edge, full-width base.
  Trapezoid: [
    [0.25, 1],
    [0.75, 1],
    [1, 0],
    [0, 0],
  ],
  NonIsoscelesTrapezoid: [
    [0.2, 1],
    [0.7, 1],
    [1, 0],
    [0, 0],
  ],
  // Inverted trapezoid: wide top, narrow base (flowchart "manual operation").
  FlowChartManualOperation: [
    [0, 1],
    [1, 1],
    [0.8, 0],
    [0.2, 0],
  ],
  // Top edge slopes down to the left (flowchart "manual input").
  FlowChartManualInput: [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 0.7],
  ],
  // Triangle pointing up / down (extract / merge).
  FlowChartExtract: [
    [0.5, 1],
    [1, 0],
    [0, 0],
  ],
  FlowChartMerge: [
    [0, 1],
    [1, 1],
    [0.5, 0],
  ],
  // Elongated hexagon (flowchart "preparation").
  FlowChartPreparation: [
    [0.15, 1],
    [0.85, 1],
    [1, 0.5],
    [0.85, 0],
    [0.15, 0],
    [0, 0.5],
  ],
  // Rectangle with a point at the bottom (off-page connector).
  FlowChartOffpageConnector: [
    [0, 1],
    [1, 1],
    [1, 0.35],
    [0.5, 0],
    [0, 0.35],
  ],
  // Five-sided home-plate arrow pointing right.
  HomePlate: [
    [0, 0],
    [0.75, 0],
    [1, 0.5],
    [0.75, 1],
    [0, 1],
  ],
  Chevron: [
    [0, 0],
    [0.55, 0],
    [1, 0.5],
    [0.55, 1],
    [0, 1],
    [0.45, 0.5],
  ],
  RightArrow: [
    [0, 0.25],
    [0.62, 0.25],
    [0.62, 0],
    [1, 0.5],
    [0.62, 1],
    [0.62, 0.75],
    [0, 0.75],
  ],
  LeftArrow: [
    [1, 0.25],
    [0.38, 0.25],
    [0.38, 0],
    [0, 0.5],
    [0.38, 1],
    [0.38, 0.75],
    [1, 0.75],
  ],
  UpArrow: [
    [0.25, 0],
    [0.25, 0.62],
    [0, 0.62],
    [0.5, 1],
    [1, 0.62],
    [0.75, 0.62],
    [0.75, 0],
  ],
  DownArrow: [
    [0.25, 1],
    [0.25, 0.38],
    [0, 0.38],
    [0.5, 0],
    [1, 0.38],
    [0.75, 0.38],
    [0.75, 1],
  ],
  // Double-headed horizontal arrow.
  LeftRightArrow: [
    [0, 0.5],
    [0.35, 1],
    [0.35, 0.75],
    [0.65, 0.75],
    [0.65, 1],
    [1, 0.5],
    [0.65, 0],
    [0.65, 0.25],
    [0.35, 0.25],
    [0.35, 0],
  ],
  // Double-headed vertical arrow.
  UpDownArrow: [
    [0.5, 1],
    [1, 0.65],
    [0.75, 0.65],
    [0.75, 0.35],
    [1, 0.35],
    [0.5, 0],
    [0, 0.35],
    [0.25, 0.35],
    [0.25, 0.65],
    [0, 0.65],
  ],
  // Four-way arrow (up/right/down/left), clockwise from the top tip.
  QuadArrow: [
    [0.5, 1],
    [0.75, 0.8],
    [0.625, 0.8],
    [0.625, 0.625],
    [0.8, 0.625],
    [0.8, 0.75],
    [1, 0.5],
    [0.8, 0.25],
    [0.8, 0.375],
    [0.625, 0.375],
    [0.625, 0.2],
    [0.75, 0.2],
    [0.5, 0],
    [0.25, 0.2],
    [0.375, 0.2],
    [0.375, 0.375],
    [0.2, 0.375],
    [0.2, 0.25],
    [0, 0.5],
    [0.2, 0.75],
    [0.2, 0.625],
    [0.375, 0.625],
    [0.375, 0.8],
    [0.25, 0.8],
  ],
  // Right arrow with a V-notch cut into the tail.
  NotchedRightArrow: [
    [0, 0.25],
    [0.15, 0.5],
    [0, 0.75],
    [0.62, 0.75],
    [0.62, 1],
    [1, 0.5],
    [0.62, 0],
    [0.62, 0.25],
  ],
  Plus: [
    [0.35, 0],
    [0.65, 0],
    [0.65, 0.35],
    [1, 0.35],
    [1, 0.65],
    [0.65, 0.65],
    [0.65, 1],
    [0.35, 1],
    [0.35, 0.65],
    [0, 0.65],
    [0, 0.35],
    [0.35, 0.35],
  ],
  MathPlus: [
    [0.35, 0],
    [0.65, 0],
    [0.65, 0.35],
    [1, 0.35],
    [1, 0.65],
    [0.65, 0.65],
    [0.65, 1],
    [0.35, 1],
    [0.35, 0.65],
    [0, 0.65],
    [0, 0.35],
    [0.35, 0.35],
  ],
  MathMinus: [
    [0, 0.42],
    [1, 0.42],
    [1, 0.58],
    [0, 0.58],
  ],
  MathMultiply: [
    [0.18, 0],
    [0.5, 0.32],
    [0.82, 0],
    [1, 0.18],
    [0.68, 0.5],
    [1, 0.82],
    [0.82, 1],
    [0.5, 0.68],
    [0.18, 1],
    [0, 0.82],
    [0.32, 0.5],
    [0, 0.18],
  ],
  // Jagged lightning bolt.
  LightningBolt: [
    [0.32, 1],
    [0.6, 1],
    [0.46, 0.62],
    [0.7, 0.62],
    [0.4, 0],
    [0.5, 0.44],
    [0.26, 0.44],
  ],
} satisfies Record<string, readonly NormalizedPoint[]>;

const roundedRectangleShapeTypes = [
  "RoundRectangle",
  "FlowChartTerminator",
  "ActionButtonBlank",
  "WedgeRRectCallout",
] as const;

/** Per-corner treatment for the rectangle family (round / snip / square). */
type CornerStyle = "square" | "round" | "snip";
interface CornerSpec {
  readonly bl: CornerStyle;
  readonly br: CornerStyle;
  readonly tr: CornerStyle;
  readonly tl: CornerStyle;
}
const SQ: CornerSpec = { bl: "square", br: "square", tr: "square", tl: "square" };

/** prst → corner treatment for snipped / partially-rounded rectangles. */
const cornerRectShapes: Record<string, CornerSpec> = {
  Round1Rectangle: { ...SQ, tr: "round" },
  Round2SameRectangle: { ...SQ, tl: "round", tr: "round" },
  Round2DiagonalRectangle: { ...SQ, tl: "round", br: "round" },
  Snip1Rectangle: { ...SQ, tr: "snip" },
  Snip2SameRectangle: { ...SQ, tl: "snip", tr: "snip" },
  Snip2DiagonalRectangle: { ...SQ, tl: "snip", br: "snip" },
  SnipRoundRectangle: { ...SQ, tl: "snip", tr: "round" },
  FlowChartPunchedCard: { ...SQ, tl: "snip" },
  // Bevel & predefined-process have no special corners; render as a plain rect
  // (the bevel/side-bar detail isn't expressible in a single flat fill).
  Bevel: SQ,
  FlowChartPredefinedProcess: SQ,
};

const ellipseShapeTypes = ["Ellipse", "FlowChartConnector"] as const;
const allRoundShapeTypes = [
  ...roundedRectangleShapeTypes,
  "FlowChartAlternateProcess",
];

const starPointCounts: Record<string, number> = {
  Star4: 4,
  Star5: 5,
  Star6: 6,
  Star7: 7,
  Star8: 8,
  Star10: 10,
  Star12: 12,
  Star16: 16,
  Star24: 24,
  Star32: 32,
};

const regularPolygonSides: Record<string, number> = {
  Pentagon: 5,
  Hexagon: 6,
  Heptagon: 7,
  Octagon: 8,
  Decagon: 10,
  Dodecagon: 12,
};

const bespokeShapeTypes = [
  "Pie",
  "Chord",
  "Heart",
  "MathEqual",
  "MathNotEqual",
  "MathDivide",
  "WedgeRectCallout",
  "WedgeEllipseCallout",
] as const;

/**
 * Mark / stamp glyphs (✓ ✗ ○ ● ◎). These are NOT PowerPoint presets — they are
 * drawn through the same generator so the driver and overlay treat them as
 * ordinary `add-shape` vector paths. `MarkCircle` (○) and `MarkDot` (●) share
 * the ellipse geometry; their only difference is the default paint (outline vs
 * fill), which the web placement layer chooses via `markDefaultStyle`.
 */
export const markStampShapeTypes = [
  "MarkCheck",
  "MarkCross",
  "MarkCircle",
  "MarkDot",
  "MarkDoubleCircle",
] as const;

/** Mark glyphs authored as stroked OPEN paths (no closed subpath). */
export const openMarkShapeTypes = ["MarkCheck", "MarkCross"] as const;

export const supportedPowerPointShapePathTypes = [
  ...Object.keys(normalizedPolygonShapes),
  ...Object.keys(regularPolygonSides),
  ...Object.keys(starPointCounts),
  ...Object.keys(cornerRectShapes),
  ...ellipseShapeTypes,
  ...allRoundShapeTypes,
  ...bespokeShapeTypes,
  ...markStampShapeTypes,
] as const;

export const supportedPowerPointShapePathTypeSet = new Set<string>(
  supportedPowerPointShapePathTypes,
);

function move(x: number, y: number): ShapePath[number] {
  return { type: "move", x, y };
}

function line(x: number, y: number): ShapePath[number] {
  return { type: "line", x, y };
}

function cubic(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x: number,
  y: number,
): ShapePath[number] {
  return { type: "cubic", x1, y1, x2, y2, x, y };
}

function close(): ShapePath[number] {
  return { type: "close" };
}

function scalePoint(bounds: ShapeBounds, point: NormalizedPoint) {
  return {
    x: bounds.x + point[0] * bounds.width,
    y: bounds.y + point[1] * bounds.height,
  };
}

function polygonPath(
  bounds: ShapeBounds,
  points: readonly NormalizedPoint[],
): ShapePath {
  const [first, ...rest] = points.map((point) => scalePoint(bounds, point));
  if (!first) return [];
  return [
    move(first.x, first.y),
    ...rest.map((point) => line(point.x, point.y)),
    close(),
  ];
}

function regularPolygonPath(
  bounds: ShapeBounds,
  sides: number,
  rotation = Math.PI / 2,
): ShapePath {
  const points: NormalizedPoint[] = Array.from(
    { length: sides },
    (_, index) => {
      const angle = rotation + (index * Math.PI * 2) / sides;
      return [0.5 + Math.cos(angle) * 0.5, 0.5 + Math.sin(angle) * 0.5];
    },
  );
  return polygonPath(bounds, points);
}

/** Inner/outer radius ratio for an N-pointed star (fuller as N grows). */
function starInnerRatio(points: number): number {
  const table: Record<number, number> = {
    4: 0.38,
    5: 0.45,
    6: 0.5,
    7: 0.55,
    8: 0.58,
    10: 0.62,
    12: 0.66,
    16: 0.72,
    24: 0.8,
    32: 0.85,
  };
  return table[points] ?? 0.5;
}

function starPath(bounds: ShapeBounds, points = 5): ShapePath {
  const innerRatio = starInnerRatio(points);
  const vertices: NormalizedPoint[] = Array.from(
    { length: points * 2 },
    (_, index) => {
      const angle = Math.PI / 2 + (index * Math.PI) / points;
      const radius = index % 2 === 0 ? 0.5 : 0.5 * innerRatio;
      return [0.5 + Math.cos(angle) * radius, 0.5 + Math.sin(angle) * radius];
    },
  );
  return polygonPath(bounds, vertices);
}

function ellipsePath(bounds: ShapeBounds): ShapePath {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const rx = bounds.width / 2;
  const ry = bounds.height / 2;
  const ox = rx * cubicCircleKappa;
  const oy = ry * cubicCircleKappa;

  return [
    move(cx + rx, cy),
    cubic(cx + rx, cy + oy, cx + ox, cy + ry, cx, cy + ry),
    cubic(cx - ox, cy + ry, cx - rx, cy + oy, cx - rx, cy),
    cubic(cx - rx, cy - oy, cx - ox, cy - ry, cx, cy - ry),
    cubic(cx + ox, cy - ry, cx + rx, cy - oy, cx + rx, cy),
    close(),
  ];
}

function roundedRectanglePath(
  bounds: ShapeBounds,
  radiusRatio = 0.18,
): ShapePath {
  const radius = Math.min(bounds.width, bounds.height) * radiusRatio;
  const x0 = bounds.x;
  const x1 = bounds.x + bounds.width;
  const y0 = bounds.y;
  const y1 = bounds.y + bounds.height;
  const ox = radius * cubicCircleKappa;
  const oy = radius * cubicCircleKappa;

  return [
    move(x0 + radius, y0),
    line(x1 - radius, y0),
    cubic(x1 - radius + ox, y0, x1, y0 + radius - oy, x1, y0 + radius),
    line(x1, y1 - radius),
    cubic(x1, y1 - radius + oy, x1 - radius + ox, y1, x1 - radius, y1),
    line(x0 + radius, y1),
    cubic(x0 + radius - ox, y1, x0, y1 - radius + oy, x0, y1 - radius),
    line(x0, y0 + radius),
    cubic(x0, y0 + radius - oy, x0 + radius - ox, y0, x0 + radius, y0),
    close(),
  ];
}

/**
 * Rectangle whose four corners are each independently squared, rounded, or
 * snipped (diagonal cut). Powers the snip/round rectangle family without a
 * bespoke generator per prst.
 */
function cornerRectPath(
  bounds: ShapeBounds,
  corners: CornerSpec,
  amountRatio = 0.33,
): ShapePath {
  const c = Math.min(bounds.width, bounds.height) * amountRatio;
  const o = c * cubicCircleKappa;
  const x0 = bounds.x;
  const x1 = bounds.x + bounds.width;
  const y0 = bounds.y; // bottom
  const y1 = bounds.y + bounds.height; // top
  const amt = (style: CornerStyle) => (style === "square" ? 0 : c);
  const cBL = amt(corners.bl);
  const cBR = amt(corners.br);
  const cTR = amt(corners.tr);
  const cTL = amt(corners.tl);
  const path: ShapePath[number][] = [move(x0 + cBL, y0)];

  // Bottom edge → BR corner.
  path.push(line(x1 - cBR, y0));
  if (corners.br === "round")
    path.push(cubic(x1 - cBR + o, y0, x1, y0 + cBR - o, x1, y0 + cBR));
  else if (corners.br === "snip") path.push(line(x1, y0 + cBR));

  // Right edge → TR corner.
  path.push(line(x1, y1 - cTR));
  if (corners.tr === "round")
    path.push(cubic(x1, y1 - cTR + o, x1 - cTR + o, y1, x1 - cTR, y1));
  else if (corners.tr === "snip") path.push(line(x1 - cTR, y1));

  // Top edge → TL corner.
  path.push(line(x0 + cTL, y1));
  if (corners.tl === "round")
    path.push(cubic(x0 + cTL - o, y1, x0, y1 - cTL + o, x0, y1 - cTL));
  else if (corners.tl === "snip") path.push(line(x0, y1 - cTL));

  // Left edge → BL corner.
  path.push(line(x0, y0 + cBL));
  if (corners.bl === "round")
    path.push(cubic(x0, y0 + cBL - o, x0 + cBL - o, y0, x0 + cBL, y0));
  else if (corners.bl === "snip") path.push(line(x0 + cBL, y0));

  path.push(close());
  return path;
}

function mathEqualPath(bounds: ShapeBounds): ShapePath {
  return [
    ...polygonPath(bounds, [
      [0, 0.22],
      [1, 0.22],
      [1, 0.38],
      [0, 0.38],
    ]),
    ...polygonPath(bounds, [
      [0, 0.62],
      [1, 0.62],
      [1, 0.78],
      [0, 0.78],
    ]),
  ];
}

function mathNotEqualPath(bounds: ShapeBounds): ShapePath {
  return [
    ...mathEqualPath(bounds),
    // A diagonal slash (thin parallelogram) over the two bars.
    ...polygonPath(bounds, [
      [0.34, 0.05],
      [0.46, 0.05],
      [0.66, 0.95],
      [0.54, 0.95],
    ]),
  ];
}

function mathDividePath(bounds: ShapeBounds): ShapePath {
  const dot = (cx: number, cy: number, r: number): ShapePath =>
    ellipsePath({
      x: bounds.x + (cx - r) * bounds.width,
      y: bounds.y + (cy - r) * bounds.height,
      width: 2 * r * bounds.width,
      height: 2 * r * bounds.height,
    });
  return [
    // Center bar.
    ...polygonPath(bounds, [
      [0.1, 0.43],
      [0.9, 0.43],
      [0.9, 0.57],
      [0.1, 0.57],
    ]),
    ...dot(0.5, 0.82, 0.09),
    ...dot(0.5, 0.18, 0.09),
  ];
}

/** Elliptical-arc segment approximated by cubic Béziers, sweeping a0→a1 (rad). */
function arcCubics(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  a0: number,
  a1: number,
): ShapePath {
  const segments = Math.max(1, Math.ceil(Math.abs(a1 - a0) / (Math.PI / 2)));
  const out: ShapePath[number][] = [];
  const delta = (a1 - a0) / segments;
  const k = (4 / 3) * Math.tan(delta / 4);
  let a = a0;
  for (let i = 0; i < segments; i += 1) {
    const b = a + delta;
    const cosA = Math.cos(a);
    const sinA = Math.sin(a);
    const cosB = Math.cos(b);
    const sinB = Math.sin(b);
    out.push(
      cubic(
        cx + rx * (cosA - k * sinA),
        cy + ry * (sinA + k * cosA),
        cx + rx * (cosB + k * sinB),
        cy + ry * (sinB - k * cosB),
        cx + rx * cosB,
        cy + ry * sinB,
      ),
    );
    a = b;
  }
  return out;
}

function piePath(bounds: ShapeBounds, a0 = -Math.PI / 2, a1 = Math.PI): ShapePath {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const rx = bounds.width / 2;
  const ry = bounds.height / 2;
  return [
    move(cx, cy),
    line(cx + rx * Math.cos(a0), cy + ry * Math.sin(a0)),
    ...arcCubics(cx, cy, rx, ry, a0, a1),
    close(),
  ];
}

function chordPath(bounds: ShapeBounds, a0 = -Math.PI / 4, a1 = (5 * Math.PI) / 4): ShapePath {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const rx = bounds.width / 2;
  const ry = bounds.height / 2;
  return [
    move(cx + rx * Math.cos(a0), cy + ry * Math.sin(a0)),
    ...arcCubics(cx, cy, rx, ry, a0, a1),
    close(),
  ];
}

function heartPath(bounds: ShapeBounds): ShapePath {
  const p = (nx: number, ny: number) => scalePoint(bounds, [nx, ny]);
  const bottom = p(0.5, 0.0);
  return [
    move(bottom.x, bottom.y),
    cubicTo(bounds, [0.0, 0.55], [0.1, 0.95], [0.5, 0.78]),
    cubicTo(bounds, [0.9, 0.95], [1.0, 0.55], [0.5, 0.0]),
    close(),
  ];
}

function cubicTo(
  bounds: ShapeBounds,
  c1: NormalizedPoint,
  c2: NormalizedPoint,
  end: NormalizedPoint,
): ShapePath[number] {
  const a = scalePoint(bounds, c1);
  const b = scalePoint(bounds, c2);
  const e = scalePoint(bounds, end);
  return cubic(a.x, a.y, b.x, b.y, e.x, e.y);
}

function wedgeRectCalloutPath(bounds: ShapeBounds): ShapePath {
  // Body rect occupies the top 70%; a triangular tail points to the lower-left.
  const body = polygonPath(bounds, [
    [0, 0.3],
    [1, 0.3],
    [1, 1],
    [0, 1],
  ]);
  const tail = polygonPath(bounds, [
    [0.2, 0.3],
    [0.45, 0.3],
    [0.05, 0],
  ]);
  return [...body, ...tail];
}

function wedgeEllipseCalloutPath(bounds: ShapeBounds): ShapePath {
  const body = ellipsePath({
    x: bounds.x,
    y: bounds.y + bounds.height * 0.28,
    width: bounds.width,
    height: bounds.height * 0.72,
  });
  const tail = polygonPath(bounds, [
    [0.25, 0.35],
    [0.5, 0.4],
    [0.05, 0],
  ]);
  return [...body, ...tail];
}

/** ✓ checkmark — an open two-segment polyline (short down-stroke, long up-stroke). */
function markCheckPath(bounds: ShapeBounds): ShapePath {
  const a = scalePoint(bounds, [0.1, 0.52]);
  const b = scalePoint(bounds, [0.42, 0.16]);
  const c = scalePoint(bounds, [0.92, 0.86]);
  return [move(a.x, a.y), line(b.x, b.y), line(c.x, c.y)];
}

/** ✗ cross — two open diagonal strokes forming an X. */
function markCrossPath(bounds: ShapeBounds): ShapePath {
  const a = scalePoint(bounds, [0.16, 0.84]);
  const b = scalePoint(bounds, [0.84, 0.16]);
  const c = scalePoint(bounds, [0.16, 0.16]);
  const d = scalePoint(bounds, [0.84, 0.84]);
  return [move(a.x, a.y), line(b.x, b.y), move(c.x, c.y), line(d.x, d.y)];
}

/** ◎ double circle — two concentric closed ellipses (outer + inset inner). */
function markDoubleCirclePath(bounds: ShapeBounds): ShapePath {
  const inset = 0.22;
  const inner: ShapeBounds = {
    x: bounds.x + bounds.width * inset,
    y: bounds.y + bounds.height * inset,
    width: bounds.width * (1 - 2 * inset),
    height: bounds.height * (1 - 2 * inset),
  };
  return [...ellipsePath(bounds), ...ellipsePath(inner)];
}

export function canCreatePowerPointShapePath(shapeType: string): boolean {
  return supportedPowerPointShapePathTypeSet.has(shapeType);
}

export function createPowerPointShapePath(
  shapeType: string,
  bounds: ShapeBounds,
  adjustments?: readonly number[],
): ShapePath | undefined {
  // Adjustable shapes (the `adj` guides) are dispatched first; their geometry is
  // a function of the adjustment values. Everything else uses fixed geometry.
  const adjusted = createAdjustableShapePath(shapeType, bounds, adjustments);
  if (adjusted) return adjusted;

  const polygonPoints =
    normalizedPolygonShapes[shapeType as keyof typeof normalizedPolygonShapes];
  if (polygonPoints) {
    return polygonPath(bounds, polygonPoints);
  }

  const corners = cornerRectShapes[shapeType];
  if (corners) return cornerRectPath(bounds, corners);

  const sides = regularPolygonSides[shapeType];
  if (sides) return regularPolygonPath(bounds, sides);

  const starPoints = starPointCounts[shapeType];
  if (starPoints) return starPath(bounds, starPoints);

  switch (shapeType) {
    case "Ellipse":
    case "FlowChartConnector":
      return ellipsePath(bounds);
    case "RoundRectangle":
      return roundedRectanglePath(bounds);
    case "FlowChartTerminator":
      return roundedRectanglePath(bounds, 0.5);
    case "FlowChartAlternateProcess":
      return roundedRectanglePath(bounds, 0.12);
    case "ActionButtonBlank":
    case "WedgeRRectCallout":
      return roundedRectanglePath(bounds, 0.16);
    case "MathEqual":
      return mathEqualPath(bounds);
    case "MathNotEqual":
      return mathNotEqualPath(bounds);
    case "MathDivide":
      return mathDividePath(bounds);
    case "Pie":
      return piePath(bounds);
    case "Chord":
      return chordPath(bounds);
    case "Heart":
      return heartPath(bounds);
    case "WedgeRectCallout":
      return wedgeRectCalloutPath(bounds);
    case "WedgeEllipseCallout":
      return wedgeEllipseCalloutPath(bounds);
    case "MarkCheck":
      return markCheckPath(bounds);
    case "MarkCross":
      return markCrossPath(bounds);
    case "MarkCircle":
    case "MarkDot":
      return ellipsePath(bounds);
    case "MarkDoubleCircle":
      return markDoubleCirclePath(bounds);
    default:
      return undefined;
  }
}

function formatNumber(value: number): string {
  return String(Number(value.toFixed(3)));
}

export function shapePathToSvgPath(
  path: ShapePath,
  pageSize: PageSize,
  zoom = 1,
): string {
  return path
    .map((command) => {
      switch (command.type) {
        case "move": {
          const point = pagePointToViewport(
            { space: "page", x: command.x, y: command.y },
            pageSize,
            zoom,
          );
          return `M ${formatNumber(point.x)} ${formatNumber(point.y)}`;
        }
        case "line": {
          const point = pagePointToViewport(
            { space: "page", x: command.x, y: command.y },
            pageSize,
            zoom,
          );
          return `L ${formatNumber(point.x)} ${formatNumber(point.y)}`;
        }
        case "cubic": {
          const control1 = pagePointToViewport(
            { space: "page", x: command.x1, y: command.y1 },
            pageSize,
            zoom,
          );
          const control2 = pagePointToViewport(
            { space: "page", x: command.x2, y: command.y2 },
            pageSize,
            zoom,
          );
          const point = pagePointToViewport(
            { space: "page", x: command.x, y: command.y },
            pageSize,
            zoom,
          );
          return [
            "C",
            formatNumber(control1.x),
            formatNumber(control1.y),
            formatNumber(control2.x),
            formatNumber(control2.y),
            formatNumber(point.x),
            formatNumber(point.y),
          ].join(" ");
        }
        case "close":
          return "Z";
      }
    })
    .join(" ");
}
